import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PAGE_HEROES } from "../content/page-copy";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { NODE_ENV_EXAMPLE } from "../generated/node-env-example";

/**
 * InstallPage — the distribution door (CO 494 Phase 4, Ron 2026-09-06).
 *
 * The portal's purpose is growing the network by getting ComOS installed on
 * hardware people own — a Mac mini is the reference machine. Voice model: the
 * FoF series (fof-01, "Every Node Is Its Own World"). Every mechanism claim
 * verified this session: the signed release manifest is live at
 * /.well-known/comos-node-releases.json; federation_node_register / _decide /
 * _status are live in production; the money-write refusal is enforced (CO 495);
 * outbound-only is rig-proven (CO 475 T-series). Installs are guided today —
 * the CTA says so honestly. No customer-node count is implied.
 */

const MANIFEST_URL =
  "https://comos-federation.com/.well-known/comos-node-releases.json";
// The public distribution repo — sealed, signed artifacts + checksums only,
// never source; the manifest above is the authoritative index into it.
const RELEASES_URL =
  "https://github.com/ronrey/comos-node-releases/releases/latest";

// The one-paste install: the hub serves how_to_about to any caller with no
// account (identity-free), and its "Self-hosted node" sections carry the full
// macOS + Linux walkthrough and the artifact README verbatim (CO 594) — so an
// agent that can run commands needs nothing else from this page.
const INSTALL_PROMPT =
  "Connect to the ComOS MCP server at https://mcp.comos-federation.com/mcp, read how_to_about, and install a ComOS node on this machine.";

const HERO = PAGE_HEROES['install']!;

/** The Mac package the signed manifest names for the stable channel (CO 588 Phase 3). */
export interface MacPackage {
  version: string;
  url: string;
  sha256: string;
  minOs: string;
}

/** Reads channels.stable.pkg from a fetched manifest; null when it names none. */
export function macPackageFrom(manifest: unknown): MacPackage | null {
  const stable = (manifest as { channels?: { stable?: Record<string, unknown> } } | null)?.channels?.stable;
  const pkg = stable?.["pkg"] as Record<string, unknown> | undefined;
  if (!stable || !pkg) return null;
  const { version } = stable;
  const { artifact, sha256, platform, min_os } = pkg;
  if (typeof version !== "string" || typeof artifact !== "string" || typeof sha256 !== "string") return null;
  if (platform !== "macos-arm64" || !artifact.startsWith("https://")) return null;
  return { version, url: artifact, sha256, minOs: typeof min_os === "string" ? min_os : "14.0" };
}

/** ComOS for Mac on its own, the manifest's top-level mac_app (CO 682 Phase 5). */
export interface MacApp {
  version: string;
  url: string;
  sha256: string;
}

/** Reads mac_app from a fetched manifest; null when it names none or names an image that is not its version's. */
export function macAppFrom(manifest: unknown): MacApp | null {
  const app = (manifest as { mac_app?: Record<string, unknown> } | null)?.mac_app;
  if (!app) return null;
  const { version, url, sha256 } = app;
  if (typeof version !== "string" || typeof url !== "string" || typeof sha256 !== "string") return null;
  if (!url.startsWith("https://") || /ComOS-(\d+\.\d+\.\d+)(?:-b\d+)?\.dmg$/.exec(url)?.[1] !== version) return null;
  return { version, url, sha256 };
}

/** The page's own origin serves the manifest; until it answers (or if it fails) there is nothing to download. */
function useRelease(): { pkg: MacPackage | null; app: MacApp | null } {
  const [release, setRelease] = useState<{ pkg: MacPackage | null; app: MacApp | null }>({ pkg: null, app: null });
  useEffect(() => {
    let live = true;
    fetch("/.well-known/comos-node-releases.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => live && setRelease({ pkg: macPackageFrom(m), app: macAppFrom(m) }))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return release;
}

/** CO 682 Phase 5 — the page leads with the app: one installer puts ComOS in Applications and a node on the Mac. */
function MacPackageCard({ pkg, app }: { pkg: MacPackage | null; app: MacApp | null }) {
  return (
    <Card accent>
      <p className="ds-eyebrow mb-3">Mac with Apple silicon</p>
      <h3 className="text-display-sm mb-2">
        {pkg ? `ComOS ${pkg.version} for Mac` : "ComOS for Mac"}
      </h3>
      <p className="text-mute dark:text-mute-soft text-sm mb-3">
        One signed, notarized installer for macOS {pkg?.minOs ?? "14"} or
        later. It puts the ComOS app in your Applications folder and a node on
        this Mac, with its own Node.js runtime, and installs Apple's Command
        Line Tools if they are missing. Then your browser opens: choose where
        your data lives (this Mac, where MongoDB is downloaded from mongodb.org
        and checked against a pinned hash, or your own MongoDB or Atlas), and
        sign in with Google. A first sign-in asks for your ComOS handle.
      </p>
      <p className="text-mute dark:text-mute-soft text-sm mb-5">
        From then on the ComOS app is where you run your nodes: start, stop
        and restart them, read their logs, back them up and restore them,
        update them, connect them to the Claude app, and add a node for
        another business. No Terminal.
      </p>
      {pkg ? (
        <>
          <a href={pkg.url} className={buttonStyles({ variant: "primary" })}>
            Download for Mac
          </a>
          <p className="text-xs text-mute dark:text-mute-soft mt-4 break-all">
            sha256 <code className="font-mono">{pkg.sha256}</code>, the same
            hash the signed manifest publishes.
          </p>
          {app && (
            <p className="text-sm text-mute dark:text-mute-soft mt-4">
              Your businesses are hosted and you want only the app?{" "}
              <a className="underline" href={app.url}>
                Download ComOS {app.version} on its own
              </a>
              .
            </p>
          )}
        </>
      ) : (
        <a
          href={RELEASES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonStyles({ variant: "primary" })}
        >
          See the releases
        </a>
      )}
    </Card>
  );
}

function InstallPromptCard() {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard
      .writeText(INSTALL_PROMPT)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  return (
    <Card accent>
      <div className="flex items-center justify-between gap-4 mb-4">
        <p className="ds-eyebrow">Paste this into your agent</p>
        <button
          type="button"
          onClick={copy}
          className="rounded-pill border border-ink/15 dark:border-paper/20 px-4 py-1.5 text-xs font-medium transition-colors hover:border-signal hover:text-signal"
        >
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </div>
      <pre className="whitespace-pre-wrap break-words rounded-xl bg-ink/[0.04] dark:bg-paper/[0.06] p-4 font-mono text-sm leading-relaxed">
        {INSTALL_PROMPT}
      </pre>
      <p className="text-sm text-mute dark:text-mute-soft mt-4">
        It works on a Mac with Apple Silicon or any Linux machine running
        systemd, a Raspberry Pi included. Everything further down this page is
        what your agent reads, if you'd rather see it or do it by hand.
      </p>
    </Card>
  );
}

export function InstallPage() {
  const { pkg, app } = useRelease();
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              A ComOS node is your world moved home: your catalog, your
              customers, your calendar, your history, and the agents that run
              the place — on your own hardware, in a room you can lock. The
              network stays what it was: one currency, one settlement system,
              one running record that proves what happened.
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mt-8 flex flex-col sm:flex-row gap-3 justify-center"
            >
              <a href="#mac" className={buttonStyles({ variant: "primary" })}>
                Download for Mac
              </a>
              <a href="#operators" className={buttonStyles({ variant: "outline" })}>
                Linux, and other ways to install
              </a>
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* ComOS for Mac — CO 588 Phase 3, CO 682 Phase 5. Link and hash come from
          the signed manifest's channels.stable.pkg, which carries the app (DECISION
          1b); mac_app is the app alone. Without a package the card links the releases. */}
      <Section id="mac">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <MacPackageCard pkg={pkg} app={app} />
          </motion.div>
        </Container>
      </Section>

      {/* What you own */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="What you own"
            title="Every node is its own world."
            lead="Every platform you've ever run on had a building you couldn't enter — the real machinery lived behind the dashboard, in their building. A node means no such building exists for your business."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-12 grid gap-5 md:grid-cols-2"
          >
            {[
              {
                k: "The living thing lives with you",
                v: "The business itself is on your machine — customers, catalog, calendar, history, and the AI staff who run the place, working for you. An export hands you a copy of your past whenever you want one.",
              },
              {
                k: "The conversation is the console",
                v: "You already run your business by talking to it: ask what came in overnight, adjust a price, chase a late shipment. Move your world onto your own machine and tomorrow morning looks exactly like this morning.",
              },
              {
                k: "No inbound doors",
                v: "The node calls the network; nothing calls in. No port forwarding, no inbound firewall rule, no router surgery — it sits behind the same ordinary router as your other machines and initiates every contact itself.",
              },
              {
                k: "Sovereign, never stranded",
                v: "Your world's operating state lives with you. The money record lives at the hub and projects down read-only — a node cannot write the ledger, by construction. That split keeps every world sovereign and the economy one.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card hover className="h-full">
                  <h3 className="text-display-sm mb-2">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* For operators — CO 682 Phase 5: everything below is the command-line
          path (Linux, or a Mac set up by hand). The one-paste agent path first:
          the prompt points at how_to_about, which any agent reads without an account. */}
      <Section id="operators">
        <Container width="prose">
          <SectionHead
            eyebrow="For operators"
            title="Linux, or a command line you'd rather drive yourself."
            lead="On a Mac, the installer above is the whole install. For a Linux machine, or a Mac you set up by hand, open an AI agent that can run commands on that machine (Claude Code works) and paste the prompt below. It reads our install instructions straight from the network and does the work, stopping for you when it needs admin rights or your Google sign-in."
          />
          <motion.div {...inView} variants={fadeUp} className="mt-10" id="agent">
            <InstallPromptCard />
          </motion.div>
        </Container>
      </Section>

      {/* What it takes */}
      <Section>
        <Container width="prose">
          <SectionHead
            eyebrow="Advanced: install from the tarball"
            title="A Mac mini on a shelf is enough. So is a Linux box."
            lead="On a Mac with Apple silicon, the ComOS installer does all of this for you. The sealed tarball is the same runtime for everything else, and for anyone who wants to run each step by hand: any Linux machine that runs systemd (a server in a closet, a VM you rent, a box under the counter) or a Mac you set up yourself."
          />
          <motion.ul
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-3"
          >
            {[
              "A Mac with Apple Silicon and FileVault on, or a Linux machine with systemd and an encrypted disk — the node's identity keypair is generated on the machine itself and its private key never leaves the device.",
              "MongoDB Community, installed by you from MongoDB's own source — Homebrew on a Mac, the mongodb.org repository on Linux — licensed to you; we never bundle someone else's database.",
              "Node.js 24 (or a patched 22 or 26: at least 22.23.4, 24.22.1 or 26.12.1) — Homebrew on a Mac; on Linux, NodeSource or your distribution's package when it is current. The installer checks the version and names what is missing.",
              "git — the node keeps a journal of its own activity in a local git repository. Command Line Tools on a Mac; apt or dnf on Linux. The installer checks for it and names it when missing.",
              "Your own LLM key — your agents run on your provider account, your bill, your choice of model.",
              "Local admin rights (sudo on Linux). Nothing else — and never an open inbound port.",
            ].map((line) => (
              <motion.li key={line} variants={fadeUp}>
                <Card className="text-mute dark:text-mute-soft text-sm">
                  {line}
                </Card>
              </motion.li>
            ))}
          </motion.ul>
        </Container>
      </Section>

      {/* The commands — CO 591. One column per OS. Every line is the one the
          artifact's generated README (CO 512) and the install runbook carry;
          re-read scripts/generate-node-readme.mjs before changing any of them. */}
      <Section tint="sunk" id="commands">
        <Container>
          <SectionHead
            eyebrow="Advanced: the commands"
            title="Two operating systems, one artifact, the same five minutes."
            lead="Install the prerequisites from their own sources, extract the verified release, copy the environment file, run the installer for your OS, then join. The README inside the artifact says the same thing in the same words."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-5 lg:grid-cols-2"
          >
            {[
              {
                os: "On a Mac",
                note: "A LaunchAgent for your user: starts at login, restarts on crash. FileVault on is the shipped posture — the installer checks and says so.",
                lines: [
                  "# MongoDB and Node.js, from their own sources",
                  "brew tap mongodb/brew && brew trust mongodb/brew",
                  "brew install mongodb-community node git",
                  "brew services start mongodb-community",
                  "",
                  "# the verified release, into its own folder",
                  "mkdir -p ~/comos-nodes/node-a",
                  "tar -xzf comos-node-<version>.tar.gz -C ~/comos-nodes/node-a",
                  "cd ~/comos-nodes/node-a && cp env.example .env",
                  "",
                  "# install, then join — a browser opens for the sign-in",
                  'bash install-launchd.sh "$(pwd)"',
                  'bash comos-node.sh "$(pwd)" join',
                  "",
                  "# day two",
                  'bash comos-node.sh "$(pwd)" status|health|logs|backup|restart|uninstall',
                ],
              },
              {
                os: "On Linux",
                note: "A system unit that runs as you: starts at boot, restarts on crash, no login needed. An encrypted disk (LUKS) is the shipped posture — the installer checks and says so. No systemd (a container, Alpine)? The same shim runs the node in the foreground.",
                lines: [
                  "# Node.js 24 and MongoDB, from their own sources (Debian/Ubuntu shown)",
                  "curl -fsSL https://deb.nodesource.com/setup_24.x | sudo -E bash -",
                  "sudo apt-get install -y nodejs git",
                  "# MongoDB: mongodb.com/docs/manual/administration/install-on-linux",
                  "#   (arm64 Debian / Raspberry Pi OS: use the Ubuntu 'noble' apt line — it is the only arm64 server)",
                  "sudo systemctl enable --now mongod",
                  "",
                  "# the verified release, into its own folder",
                  "mkdir -p ~/comos-nodes/node-a",
                  "tar -xzf comos-node-<version>.tar.gz -C ~/comos-nodes/node-a",
                  "cd ~/comos-nodes/node-a && cp env.example .env",
                  "",
                  "# install (root writes the unit), then join",
                  'sudo bash install-systemd.sh "$(pwd)"',
                  'bash comos-node.sh "$(pwd)" join',
                  "",
                  "# a container or a host with no systemd? run it in the foreground",
                  "#   (same checks; your container's init restarts it):",
                  'bash comos-node.sh "$(pwd)" run',
                  "",
                  "# no browser on this box? forward the sign-in to your laptop:",
                  "#   ssh -L 9199:127.0.0.1:9199 <the box>   (from the laptop)",
                  'bash comos-node.sh "$(pwd)" join --no-browser --callback-port 9199',
                  "",
                  "# day two — start, stop, restart and uninstall need sudo",
                  'bash comos-node.sh "$(pwd)" status|health|logs|backup',
                  'sudo bash comos-node.sh "$(pwd)" restart|stop|start|uninstall',
                ],
              },
            ].map((col) => (
              <motion.div key={col.os} variants={fadeUp}>
                <Card className="h-full overflow-x-auto">
                  <h3 className="text-display-sm mb-1.5">{col.os}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm mb-4">
                    {col.note}
                  </p>
                  <pre className="text-xs leading-relaxed whitespace-pre font-mono text-mute dark:text-mute-soft">
                    {col.lines.join("\n")}
                  </pre>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <motion.p
            {...inView}
            variants={fadeUp}
            className="mt-8 text-sm text-mute dark:text-mute-soft"
          >
            Before extracting, check the download against the signed manifest:{" "}
            <code className="font-mono">shasum -a 256 comos-node-&lt;version&gt;.tar.gz</code>{" "}
            on a Mac, <code className="font-mono">sha256sum</code> on Linux — the
            hash must equal the one the manifest publishes for that version. The
            join signs you in with Google (a first sign-in asks for your ComOS handle — permanent, and the first half of every business name: vnm-sport), registers the node under your account,
            proves its key, and the network admits it on the spot once your
            account is in good standing. Nothing to approve by hand, on either
            OS.
          </motion.p>
        </Container>
      </Section>

      {/* The environment — CO 511. Rendered from the generated module (one
          source with the artifact's env.example and the how-to info tenant);
          never hand-edit the template text here. */}
      <Section id="environment">
        <Container width="prose">
          <SectionHead
            eyebrow="The environment"
            title="One file decides where everything runs."
            lead="A node is configured entirely by its .env — the artifact ships env.example beside the installer, and the installer refuses to run until you've copied it to .env and filled it in. Where state lives is your dial: MONGODB_URI takes the local mongod on your disk, a server you run elsewhere, or a managed Atlas string — one URL, zero code change. Atlas-backed means self-hosted compute with managed data; the local mongod keeps everything on the box."
          />
          <motion.div {...inView} variants={fadeUp} className="mt-10">
            <Card className="overflow-x-auto">
              <pre className="text-xs leading-relaxed whitespace-pre font-mono text-mute dark:text-mute-soft">
                {NODE_ENV_EXAMPLE}
              </pre>
            </Card>
          </motion.div>
        </Container>
      </Section>

      {/* How the install goes */}
      <Section tint="sunk" id="how">
        <Container width="prose">
          <SectionHead
            eyebrow="How the install goes"
            title="Five steps, and the last one is the network's."
          />
          <motion.ol
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-4"
          >
            {[
              {
                k: "1 · Join the network — free",
                v: "A node is your world moving home, so the world exists first. The Start door admits you as a manager — under the handle you choose, which names every business you open (<handle>-<business>) — in minutes and costs nothing; run hosted for a day or a year before moving.",
              },
              {
                k: "2 · Fetch the signed release",
                v: "Every version of the node runtime ships on a signed release train. Fetch the manifest, download the sealed artifact, verify the checksum — an artifact that doesn't match refuses to run.",
              },
              {
                k: "3 · Boot on your machine",
                v: "The node binds to your machine only, mints its keypair on the device, and starts calling the network on its own clock. Nothing listens for the outside world.",
              },
              {
                k: "4 · Get admitted",
                v: "Run join: the node registers under your own account and proves its key, and the network admits it on the spot once your account is in good standing. No one has to approve it by hand.",
              },
              {
                k: "5 · Your world reports in",
                v: "From then on the node reports in on cadence, collects the work brokered to it, and completes it on your hardware. Updates ride the same signed train — and updates are free, forever.",
              },
            ].map((it) => (
              <motion.li key={it.k} variants={fadeUp}>
                <Card className="h-full">
                  <h3 className="text-display-sm mb-1.5">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.li>
            ))}
          </motion.ol>
          <motion.p
            {...inView}
            variants={fadeUp}
            className="mt-8 text-sm text-mute dark:text-mute-soft"
          >
            Artifacts live in the public{" "}
            <a
              className="underline"
              href="https://github.com/ronrey/comos-node-releases/releases"
              target="_blank"
              rel="noopener noreferrer"
            >
              comos-node-releases
            </a>{" "}
            repo — sealed builds and checksums only, never source. The signed
            manifest is live and machine-readable:{" "}
            <a
              className="underline break-all"
              href={MANIFEST_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {MANIFEST_URL}
            </a>{" "}
            — versions, artifacts, checksums, and the network minimum.
          </motion.p>
        </Container>
      </Section>

      {/* Status + door */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <h2 className="text-display-md text-balance">
              The release train is public.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-5">
              Every version is a sealed, signed artifact in the public releases
              repo — download it, verify the checksum against the manifest, and
              boot. A reference rig runs the train around the clock. Want a
              hand? We'll walk your install with you personally — write{" "}
              <a
                className="underline"
                href="mailto:ron@comos-federation.com?subject=Install%20ComOS%20on%20my%20machine"
              >
                ron@comos-federation.com
              </a>{" "}
              and we'll put your world on your machine together.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="#mac"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                Download ComOS
              </a>
              <Link
                to="/start"
                className={buttonStyles({ variant: "outline", size: "lg" })}
              >
                Start free in the cloud
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>

      {/* Close */}
      <Section tint="ink">
        <Container className="text-center">
          <motion.div {...inView} variants={fadeUp} className="max-w-2xl mx-auto">
            <h2 className="text-display-lg text-balance text-paper">
              Growth by welcoming worlds, never by absorbing them.
            </h2>
            <p className="text-lead text-paper/70 mt-6">
              The edge of this network is a map of small empires — each one
              grown by its owner's judgment, at its owner's pace, anchored to
              one economy. Put one on your desk.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="#mac"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                Download ComOS
              </a>
              <Link
                to="/blog/every-node-is-its-own-world"
                className={buttonStyles({
                  variant: "outline",
                  size: "lg",
                  className:
                    "border-paper/25 text-paper hover:text-white hover:border-paper",
                })}
              >
                The series behind it
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
